import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, money2, num, fiscalMonthLabel, TIER_LABELS, TIER_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FlaskConical, Save, RotateCcw, Play, Trash2 } from "lucide-react";

const MONTH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function seedRows(actualsRows) {
  return (actualsRows || []).map((r) => {
    const months = {};
    MONTH_KEYS.forEach((m) => { months[m] = { basic: 0, nett: 0, noncomm: 0, recovery: 0, debtor: false, jnr_staff: 0 }; });
    (r.quarters || []).forEach((q) => (q.months || []).forEach((m) => {
      months[m.month] = { basic: m.basic || 0, nett: m.nett || 0, noncomm: m.noncomm || 0, recovery: m.recovery || 0, debtor: !!m.debtor, jnr_staff: m.jnr_staff || 0 };
    }));
    const recovery_label = (r.quarters || []).map((q) => q.recovery_label).find((x) => x) || "";
    return { user_id: r.user_id, name: r.name, title: r.title, eligible: r.eligible, months, carry_in: {}, recovery_label };
  });
}

const CFG_FIELDS = [
  ["threshold_multiple", "Break-even ×"],
  ["standard_min", "Standard ×"],
  ["top_min", "Top ×"],
  ["standard_rate", "Std rate"],
  ["top_rate", "Top rate"],
  ["rehab_rate", "Rehab rate"],
  ["cap_multiple", "Cap ×"],
];

export default function ScenarioLab({ fy, startMonth, actualsRows, actualConfig }) {
  const [name, setName] = useState("New scenario");
  const [scenarioId, setScenarioId] = useState(null);
  const [rows, setRows] = useState(() => seedRows(actualsRows));
  const [cfg, setCfg] = useState(actualConfig || {});
  const [applyDebtor, setApplyDebtor] = useState(true);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [quarter, setQuarter] = useState(1);
  const [busy, setBusy] = useState(false);

  // load saved scenarios for this fiscal year
  useEffect(() => {
    if (!fy) return;
    api.get("/scenarios", { params: { fiscal_year: fy } }).then((r) => setSaved(r.data)).catch(() => {});
  }, [fy]);

  // initial simulate of the seeded actuals
  useEffect(() => {
    api.post("/commission/simulate", { config: actualConfig || {}, apply_debtor_rule: true, rows: seedRows(actualsRows) })
      .then((r) => setResult(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshList = () => api.get("/scenarios", { params: { fiscal_year: fy } }).then((r) => setSaved(r.data)).catch(() => {});

  const runSim = async (theRows = rows, theCfg = cfg, dbtr = applyDebtor) => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission/simulate", { config: theCfg, apply_debtor_rule: dbtr, rows: theRows });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setMonth = (uid, m, key, val) => {
    setRows((prev) => prev.map((r) => r.user_id === uid ? { ...r, months: { ...r.months, [m]: { ...r.months[m], [key]: val } } } : r));
  };
  const setCarry = (uid, q, val) => {
    setRows((prev) => prev.map((r) => r.user_id === uid ? { ...r, carry_in: { ...(r.carry_in || {}), [q]: val === "" ? undefined : (parseFloat(val || 0) || 0) } } : r));
  };
  const setCfgField = (k, v) => setCfg((p) => ({ ...p, [k]: parseFloat(v) }));

  const newFromActuals = () => {
    const s = seedRows(actualsRows);
    setRows(s); setCfg(actualConfig || {}); setApplyDebtor(true); setScenarioId(null); setName("New scenario");
    runSim(s, actualConfig || {}, true);
    toast.success("Loaded a fresh copy of the actuals");
  };

  const save = async () => {
    const payload = { rows, cfg, applyDebtor };
    try {
      if (scenarioId) {
        await api.put(`/scenarios/${scenarioId}`, { name, fiscal_year: fy, payload });
      } else {
        const { data } = await api.post("/scenarios", { name, fiscal_year: fy, payload });
        setScenarioId(data.id);
      }
      toast.success("Scenario saved");
      refreshList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = async (id) => {
    try {
      const { data } = await api.get(`/scenarios/${id}`);
      const p = data.payload || {};
      const r = p.rows || seedRows(actualsRows);
      const c = p.cfg || actualConfig || {};
      const d = p.applyDebtor != null ? p.applyDebtor : true;
      setRows(r); setCfg(c); setApplyDebtor(d); setName(data.name); setScenarioId(data.id);
      runSim(r, c, d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async () => {
    if (!scenarioId) return;
    try { await api.delete(`/scenarios/${scenarioId}`); toast.success("Scenario deleted"); setScenarioId(null); refreshList(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const midx = [(quarter - 1) * 3 + 1, (quarter - 1) * 3 + 2, (quarter - 1) * 3 + 3];
  const labels = midx.map((m) => fiscalMonthLabel(m, startMonth));
  const totals = result?.totals || {};
  const resultFor = (uid) => result?.rows?.find((x) => x.user_id === uid)?.quarters?.[quarter - 1];

  return (
    <div className="space-y-4">
      <div className="border border-brand/40 bg-brand/5 p-4 flex flex-wrap items-center gap-3" data-testid="scenario-bar">
        <FlaskConical className="h-5 w-5 text-brand" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Scenario name</div>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1 h-9 max-w-xs" data-testid="scenario-name" />
        </div>
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Saved</div>
          <Select value={scenarioId || "none"} onValueChange={(v) => v !== "none" && load(v)}>
            <SelectTrigger className="w-[200px] rounded-none h-9" data-testid="scenario-load"><SelectValue placeholder="Load saved…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— select —</SelectItem>
              {saved.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" size="sm" className="rounded-none gap-2" onClick={newFromActuals} data-testid="scenario-new"><RotateCcw className="h-4 w-4" /> New from actuals</Button>
          <Button size="sm" className="rounded-none gap-2" onClick={() => runSim()} disabled={busy} data-testid="scenario-run"><Play className="h-4 w-4" /> Recalculate</Button>
          <Button variant="outline" size="sm" className="rounded-none gap-2" onClick={save} data-testid="scenario-save"><Save className="h-4 w-4" /> Save</Button>
          {scenarioId && <Button variant="outline" size="sm" className="rounded-none gap-2 text-destructive" onClick={del} data-testid="scenario-delete"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Badge variant="outline" className="rounded-none bg-brand/10 text-brand border-brand/30">SANDBOX</Badge>
        Changes here never affect your actual banking or commissions — experiment freely.
      </div>

      {/* config + period controls */}
      <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
        {CFG_FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
            <Input type="number" step="0.01" value={cfg[k] ?? ""} onChange={(e) => setCfgField(k, e.target.value)} className="w-20 rounded-none h-8 font-mono text-xs" data-testid={`scn-cfg-${k}`} />
          </div>
        ))}
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Debtors 90+</div>
          <div className="h-8 flex items-center gap-2"><Switch checked={applyDebtor} onCheckedChange={setApplyDebtor} data-testid="scn-debtor" /><span className="text-xs font-mono">{applyDebtor ? "On" : "Off"}</span></div>
        </div>
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Quarter</div>
          <Select value={String(quarter)} onValueChange={(v) => setQuarter(parseInt(v))}>
            <SelectTrigger className="w-[110px] rounded-none h-8" data-testid="scn-quarter"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Scenario banked (yr)</div><div className="font-mono text-2xl mt-1">{money(totals.banked)}</div></div>
        <div className="border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Scenario commission (yr)</div><div className="font-mono text-2xl mt-1 text-brand">{money(totals.commission)}</div></div>
        <div className="border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Carry-over (yr)</div><div className="font-mono text-2xl mt-1 text-warning">{money(totals.carry_out)}</div></div>
      </div>

      <div className="border border-border bg-card overflow-x-auto" data-testid="scenario-table">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Fee earner</TableHead>
              {labels.map((l, i) => (
                <TableHead key={l} className="text-center">
                  <div>{l}</div>
                  <div className="text-[9px] font-normal normal-case text-muted-foreground/70">basic · comm · non-comm · recovery · 90+</div>
                </TableHead>
              ))}
              <TableHead className="text-right">Mult.</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead className="text-right">Commission</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5 + labels.length} className="text-center py-10 text-muted-foreground">No earners to model.</TableCell></TableRow>}
            {rows.map((r) => {
              const res = resultFor(r.user_id);
              return (
                <TableRow key={r.user_id} className="hover:bg-secondary/40 align-top" data-testid={`scn-row-${r.user_id}`}>
                  <TableCell className="font-medium align-top">{r.name}<div className="text-[10px] text-muted-foreground">{r.title}</div>
                    <div className="mt-1 flex items-center gap-1" title="Override the opening carry-in for this quarter (leave blank to auto-carry from the previous quarter)">
                      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">carry-in Q{quarter}</span>
                      <input type="number" value={r.carry_in?.[quarter] ?? ""} onChange={(e) => setCarry(r.user_id, quarter, e.target.value)} placeholder="auto" data-testid={`scn-carry-${r.user_id}`} className="w-20 text-right font-mono text-[10px] border border-border bg-background px-1 py-0.5 focus:border-brand outline-none" />
                    </div>
                    <input type="text" value={r.recovery_label ?? ""} onChange={(e) => setRows((prev) => prev.map((x) => x.user_id === r.user_id ? { ...x, recovery_label: e.target.value } : x))} placeholder="recovery label" title="Label for the recovery line" data-testid={`scn-recovery-label-${r.user_id}`} className="mt-1 block w-32 text-[10px] border border-positive/40 bg-positive/5 px-1.5 py-1 focus:border-brand outline-none" />
                  </TableCell>
                  {midx.map((m) => (
                    <TableCell key={m} className="align-top">
                      <div className="flex flex-col gap-1 items-end">
                        <input type="number" value={r.months[m]?.basic ?? 0} onChange={(e) => setMonth(r.user_id, m, "basic", parseFloat(e.target.value || 0) || 0)} placeholder="basic" data-testid={`scn-basic-${r.user_id}-${m}`} className="w-28 text-right font-mono text-xs border border-border bg-background px-1.5 py-1 focus:border-brand outline-none" />
                        <input type="number" value={r.months[m]?.nett ?? 0} onChange={(e) => setMonth(r.user_id, m, "nett", parseFloat(e.target.value || 0) || 0)} placeholder="banking – comm" title="Commissionable" data-testid={`scn-nett-${r.user_id}-${m}`} className="w-28 text-right font-mono text-xs border border-brand/40 bg-secondary/40 px-1.5 py-1 focus:border-brand outline-none" />
                        <input type="number" value={r.months[m]?.noncomm ?? 0} onChange={(e) => setMonth(r.user_id, m, "noncomm", parseFloat(e.target.value || 0) || 0)} placeholder="banking – non-comm" title="Non-commissionable (not counted)" data-testid={`scn-noncomm-${r.user_id}-${m}`} className="w-28 text-right font-mono text-xs border border-dashed border-border bg-background px-1.5 py-1 focus:border-brand outline-none" />
                        <input type="number" value={r.months[m]?.recovery ?? 0} onChange={(e) => setMonth(r.user_id, m, "recovery", parseFloat(e.target.value || 0) || 0)} placeholder="recovery / other" title="Recovery / other — counts toward commission but not added to the banked total" data-testid={`scn-recovery-${r.user_id}-${m}`} className="w-28 text-right font-mono text-xs border border-positive/40 bg-positive/5 px-1.5 py-1 focus:border-brand outline-none" />
                        <label className="flex items-center gap-1 text-[10px] text-muted-foreground"><input type="checkbox" checked={!!r.months[m]?.debtor} onChange={(e) => setMonth(r.user_id, m, "debtor", e.target.checked)} data-testid={`scn-debtor-${r.user_id}-${m}`} /> 90+</label>
                      </div>
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-mono text-xs align-top">{res ? `${num(res.multiple, 2)}x` : "—"}</TableCell>
                  <TableCell className="align-top">{res && <Badge variant="outline" className={`rounded-none text-[9px] ${TIER_COLORS[res.tier] || ""}`}>{TIER_LABELS[res.tier] || res.tier}</Badge>}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-brand align-top">{res ? money2(res.commission) : "—"}{res?.capped && <span className="ml-1 text-[9px] text-warning">CAP</span>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
